import React, { useState, useEffect, useMemo } from "react";
import {
  TextField,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { IoMdSearch, IoMdClose } from "react-icons/io";

// Module-level shared cache so all instances share countries and IP detection
let cachedCountries = null;
let countriesPromise = null;
let cachedCountryCode = "EG";
let ipPromise = null;

const fetchCountriesList = async () => {
  if (cachedCountries) return cachedCountries;
  if (countriesPromise) return countriesPromise;

  countriesPromise = (async () => {
    try {
      const res = await fetch("https://py.qubx3d.com/api/v2/countries/");
      const data = await res.json();
      const results = Array.isArray(data) ? data : data?.results || [];
      cachedCountries = results;
      return results;
    } catch (err) {
      console.error("Failed to fetch countries", err);
      return [];
    } finally {
      countriesPromise = null;
    }
  })();

  return countriesPromise;
};

const fetchDetectedCountry = async () => {
  if (cachedCountryCode && cachedCountryCode !== "EG") return cachedCountryCode;
  if (ipPromise) return ipPromise;

  ipPromise = (async () => {
    try {
      const res = await fetch("https://py.qubx3d.com/api/v2/ip-info/");
      const data = await res.json();
      if (data?.country_code) {
        cachedCountryCode = data.country_code;
        return data.country_code;
      }
      return "EG";
    } catch (err) {
      return "EG";
    } finally {
      ipPromise = null;
    }
  })();

  return ipPromise;
};

const CountryPhoneInput = ({
  label = "Phone",
  value = "",
  onChange,
  className = "",
  size = "small",
  variant = "outlined",
  fullWidth = true,
  disabled = false,
  required = false,
  placeholder = "100 123 4567",
}) => {
  const [countries, setCountries] = useState(cachedCountries || []);
  const [loading, setLoading] = useState(!cachedCountries);
  const [openDialog, setOpenDialog] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState("");

  // Load countries and IP info on mount
  useEffect(() => {
    let isMounted = true;
    (async () => {
      const [list, detectedCode] = await Promise.all([
        fetchCountriesList(),
        fetchDetectedCountry(),
      ]);
      if (isMounted) {
        setCountries(list);
        setLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  // Sync internal state when external 'value' or 'countries' change
  useEffect(() => {
    if (!countries.length) return;

    if (!value) {
      setPhoneNumber("");
      if (!selectedCountry) {
        const defaultCountry =
          countries.find(
            (c) =>
              c.country_code?.toLowerCase() ===
              (cachedCountryCode || "EG").toLowerCase()
          ) ||
          countries.find(
            (c) =>
              c.country_code?.toLowerCase() === "eg" ||
              c.country_name?.toLowerCase() === "egypt"
          ) ||
          countries[0];
        setSelectedCountry(defaultCountry);
      }
      return;
    }

    // Sort by descending phone_code length to match longest prefix first (+971 before +9)
    const sorted = [...countries].sort(
      (a, b) => (b.phone_code?.length || 0) - (a.phone_code?.length || 0)
    );

    const matched = sorted.find(
      (c) => c.phone_code && value.startsWith(c.phone_code)
    );

    if (matched) {
      setSelectedCountry(matched);
      const remainder = value.slice(matched.phone_code.length);
      setPhoneNumber(remainder);
    } else {
      if (!selectedCountry) {
        const defaultCountry =
          countries.find(
            (c) =>
              c.country_code?.toLowerCase() ===
              (cachedCountryCode || "EG").toLowerCase()
          ) ||
          countries.find(
            (c) =>
              c.country_code?.toLowerCase() === "eg" ||
              c.country_name?.toLowerCase() === "egypt"
          ) ||
          countries[0];
        setSelectedCountry(defaultCountry);
      }
      const clean = value.replace(/^0+/, "");
      setPhoneNumber(clean);
    }
  }, [value, countries]);

  // Handle phone digits typing
  const handleNumberChange = (e) => {
    const raw = e.target.value;
    const digitsOnly = raw.replace(/\D/g, "");

    const isEgypt =
      selectedCountry?.phone_code === "+20" ||
      selectedCountry?.country_code?.toLowerCase() === "eg";
    const cleanNumber = isEgypt ? digitsOnly.replace(/^0+/, "") : digitsOnly;

    setPhoneNumber(cleanNumber);

    if (onChange) {
      if (cleanNumber && selectedCountry?.phone_code) {
        onChange(`${selectedCountry.phone_code}${cleanNumber}`);
      } else {
        onChange("");
      }
    }
  };

  // Handle selecting a country from the dialog
  const handleSelectCountry = (country) => {
    setSelectedCountry(country);
    setOpenDialog(false);
    setSearchText("");

    if (phoneNumber && onChange) {
      const isEgypt =
        country?.phone_code === "+20" ||
        country?.country_code?.toLowerCase() === "eg";
      const cleanNumber = isEgypt ? phoneNumber.replace(/^0+/, "") : phoneNumber;
      onChange(`${country.phone_code}${cleanNumber}`);
    }
  };

  // Filter countries for search dialog
  const filteredCountries = useMemo(() => {
    if (!searchText.trim()) return countries;
    const q = searchText.toLowerCase().trim();
    return countries.filter(
      (c) =>
        c.country_name?.toLowerCase().includes(q) ||
        c.phone_code?.toLowerCase().includes(q) ||
        c.country_code?.toLowerCase().includes(q)
    );
  }, [countries, searchText]);

  return (
    <>
      <TextField
        fullWidth={fullWidth}
        size={size}
        variant={variant}
        label={label}
        value={phoneNumber}
        onChange={handleNumberChange}
        placeholder={placeholder}
        className={className}
        disabled={disabled}
        required={required}
        type="tel"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" sx={{ mr: 1 }}>
              <Button
                type="button"
                onClick={() => setOpenDialog(true)}
                disabled={disabled}
                sx={{
                  minWidth: "auto",
                  padding: "2px 8px",
                  textTransform: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  color: "text.primary",
                  borderRight: "1px solid #d0d7de",
                  borderRadius: 0,
                  height: "28px",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                {selectedCountry ? (
                  <>
                    <img
                      src={`https://flagcdn.com/w40/${selectedCountry.country_code.toLowerCase()}.png`}
                      alt={selectedCountry.country_name}
                      style={{
                        width: "20px",
                        height: "14px",
                        objectFit: "cover",
                        borderRadius: "2px",
                        boxShadow: "0 0 1px rgba(0,0,0,0.3)",
                      }}
                    />
                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight: 600,
                        letterSpacing: "0.2px",
                      }}
                    >
                      {selectedCountry.phone_code}
                    </span>
                    <span
                      style={{
                        fontSize: "9px",
                        color: "#777",
                        marginLeft: "-2px",
                      }}
                    >
                      ▼
                    </span>
                  </>
                ) : (
                  <span style={{ fontSize: "12px", color: "#666" }}>
                    {loading ? "..." : "+Code ▼"}
                  </span>
                )}
              </Button>
            </InputAdornment>
          ),
        }}
      />

      {/* Country Selection Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="xs"
        fullWidth
        sx={{ zIndex: 1500 }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: 600,
            fontSize: "16px",
          }}
        >
          Select Country
          <IconButton
            size="small"
            onClick={() => setOpenDialog(false)}
            sx={{ color: "grey.500" }}
          >
            <IoMdClose />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          {/* Search bar */}
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Search country or code..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IoMdSearch style={{ color: "#888", fontSize: "18px" }} />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          {/* Country List */}
          <Box sx={{ maxHeight: "320px", overflowY: "auto", pr: 0.5 }}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : filteredCountries.length === 0 ? (
              <Typography
                sx={{
                  textAlign: "center",
                  py: 3,
                  color: "text.secondary",
                  fontSize: "14px",
                }}
              >
                No countries found
              </Typography>
            ) : (
              filteredCountries.map((c) => {
                const isSelected = selectedCountry?.id === c.id;
                return (
                  <Box
                    key={c.id || c.country_code}
                    onClick={() => handleSelectCountry(c)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: 1.2,
                      my: 0.5,
                      borderRadius: "6px",
                      cursor: "pointer",
                      border: isSelected
                        ? "1px solid #1976d2"
                        : "1px solid #f0f0f0",
                      backgroundColor: isSelected
                        ? "rgba(25, 118, 210, 0.08)"
                        : "transparent",
                      transition: "all 0.15s ease",
                      "&:hover": {
                        backgroundColor: isSelected
                          ? "rgba(25, 118, 210, 0.14)"
                          : "rgba(0, 0, 0, 0.04)",
                        transform: "translateX(2px)",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <img
                        src={`https://flagcdn.com/w40/${c.country_code.toLowerCase()}.png`}
                        alt={c.country_name}
                        style={{
                          width: "24px",
                          height: "16px",
                          objectFit: "cover",
                          borderRadius: "2px",
                          boxShadow: "0 0 1px rgba(0,0,0,0.3)",
                        }}
                      />
                      <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                        {c.country_name}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#2e7d32",
                        backgroundColor: "#edf7ed",
                        px: 1,
                        py: 0.2,
                        borderRadius: "4px",
                      }}
                    >
                      {c.phone_code}
                    </Typography>
                  </Box>
                );
              })
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CountryPhoneInput;
